import {
    Box,
    Card,
    Dialog,
    Icon,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { EXPERIMENT_STATUS, PLOTMAP_STATUS } from "./constants";
import { useFetch, useIsBasicRole, useSnackbar, useUsers } from "../../hooks";
import { useMemo, useState } from "react";

import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { BACKEND_ROUTES } from "../../backendRoutes";
import { CLOVERFIELD_MAP } from "../../constants";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import { FRONTEND_ROUTES } from "../../frontendRoutes";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import { Link } from "react-router-dom";
import MapIcon from "@mui/icons-material/Map";
import PublicIcon from "@mui/icons-material/Public";
import { SiteCreateForm } from "./SiteCreateForm";
import { SiteTableSkeleton } from "./SiteTableSkeleton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { iso31661 as countriesArray } from "iso-3166";
import { getDataGridHeight } from "../utils";
import useSWR from "swr";

export function SiteTable({ contract, hasLiteralTemplates }) {
    const { patch } = useFetch();
    const { openSnackbar } = useSnackbar();
    const isBasicRole = useIsBasicRole();
    const { destroy } = useFetch();

    const [pagination, setPage] = useState({
        page: 0,
        pageSize: pageSizeOptions[0],
    });
    const [isOpen, setIsOpen] = useState(false);
    const [visibilityModel, setVisibilityModel] = useState({
        actions: true,
        name: true,
        displayName: true,
        country: false,
        processingLead: false,
        experimentStatusLabel: true,
        plotmapStatusLabel: true,
    });
    const {
        data: users,
        error: usersError,
        isLoading: areUsersLoading,
    } = useUsers();
    const {
        data: sites,
        error: sitesError,
        mutate,
        isLoading: areSitesLoading,
    } = useSWR(
        `${
            BACKEND_ROUTES.SITE
        }?parentInfo=true&contractUuid=${contract.uuid}&limit=${pagination.pageSize}&offset=${
            pagination.page * pagination.pageSize
        }`,
        { keepPreviousData: true }
    );

    const rows = useMemo(
        () =>
            sites?.rows.map((site) => ({
                id: site.uuid,
                uuid: site.uuid,
                name: site.name,
                displayName: site.displayName,
                processingLead: `${site.ProcessingLead.firstName} ${site.ProcessingLead.lastName}`,
                processingLeadUuid: site.processingLeadUuid,
                country: site.countryCode
                    ? (countriesArray.find(
                          (country) => country.alpha3 === site.countryCode
                      )?.name ?? "Unknown country")
                    : "No country",
                cloverfieldUuid: site.cloverfieldUuid,
                experimentStatusLabel:
                    EXPERIMENT_STATUS[site.experimentStatus].label,
                plotmapStatusLabel: PLOTMAP_STATUS[site.plotmapStatus].label,
                experimentStatus: site.experimentStatus,
                plotmapStatus: site.plotmapStatus,
            })),
        [sites]
    );

    const columns = useMemo(
        () => [
            {
                field: "actions",
                headerName: "Actions",
                type: "actions",
                minWidth: 240,
                flex: 2,
                getActions: (params) => {
                    const onClickDelete = async () => {
                        const deleteResult = await destroy(
                            `${BACKEND_ROUTES.SITE}/${params.row.id}`
                        );
                        if (deleteResult) {
                            mutate();
                            openSnackbar(
                                `Site deleted successfully.`,
                                "success"
                            );
                        }
                    };

                    const isCloverfieldLinkDisabled =
                        !params.row.cloverfieldUuid || !CLOVERFIELD_MAP;

                    const actions = [
                        <Tooltip title="View details">
                            <GridActionsCellItem
                                label="View details"
                                icon={
                                    <VisibilityIcon
                                        fontSize="small"
                                        color="primary"
                                    />
                                }
                                component={Link}
                                to={`/${FRONTEND_ROUTES.ORDERS}/${FRONTEND_ROUTES.CONTRACT}/${contract.uuid}/${FRONTEND_ROUTES.SITE}/${params.row.id}`}
                            />
                        </Tooltip>,
                        ...(!isBasicRole
                            ? [
                                  <Tooltip title="Go to Plot Map Tool">
                                      <GridActionsCellItem
                                          label="Go to Plot Map Tool"
                                          icon={
                                              <PublicIcon
                                                  fontSize="small"
                                                  color="primary"
                                              />
                                          }
                                          component={Link}
                                          to={`/${FRONTEND_ROUTES.PARCELLAIRE}?siteUuid=${params.row.id}&fastOrthoimage=true`}
                                      />
                                  </Tooltip>,
                              ]
                            : []),
                        <Tooltip title="Go to Cloverfield site">
                            <GridActionsCellItem
                                label="Go to Cloverfield site"
                                icon={
                                    <Icon
                                        fontSize="small"
                                        sx={{
                                            filter: isCloverfieldLinkDisabled
                                                ? "grayscale(100%)"
                                                : "none",
                                        }}
                                    >
                                        <img
                                            alt="Cloverfield logo"
                                            src="/static/cloverfield_logo_green.png"
                                        />
                                    </Icon>
                                }
                                component={Link}
                                disabled={isCloverfieldLinkDisabled}
                                to={`${CLOVERFIELD_MAP}/${params.row.cloverfieldUuid}`}
                            />
                        </Tooltip>,
                        ...(!isBasicRole
                            ? [
                                  <GridActionsCellItem
                                      label="Request fieldmap drawing"
                                      icon={<EditLocationAltIcon />}
                                      disabled={params.row.plotmapStatus !== 0}
                                      onClick={async () => {
                                          const updatedSite = await patch(
                                              `${BACKEND_ROUTES.SITE}/${params.row.id}`,
                                              {
                                                  body: {
                                                      experimentStatus: 1, // Draft
                                                  },
                                              }
                                          );

                                          if (updatedSite) {
                                              openSnackbar(
                                                  `Site ${updatedSite.displayName}: field map status succesfully set to ${EXPERIMENT_STATUS[1].label}`,
                                                  "success"
                                              );
                                              mutate();
                                          }
                                      }}
                                      showInMenu
                                  />,
                                  <GridActionsCellItem
                                      label="Request plotmap validation"
                                      icon={<ChecklistRtlIcon />}
                                      disabled={params.row.experimentStatus < 3}
                                      onClick={async (e) => {
                                          const updatedSite = await patch(
                                              `${BACKEND_ROUTES.SITE}/${params.row.id}`,
                                              {
                                                  body: {
                                                      plotmapStatus: 1, // Waiting for validation
                                                  },
                                              }
                                          );

                                          if (updatedSite) {
                                              openSnackbar(
                                                  `Site ${updatedSite.displayName}: plot map status succesfully set to ${PLOTMAP_STATUS[1].label}`,
                                                  "success"
                                              );
                                              mutate();
                                          }
                                      }}
                                      showInMenu
                                  />,
                                  <GridActionsCellItem
                                      label="Copy site uuid"
                                      icon={<ContentCopyIcon />}
                                      onClick={() => {
                                          navigator.clipboard.writeText(
                                              params.row.id
                                          );
                                      }}
                                      showInMenu
                                  />,
                                  <GridActionsCellItem
                                      label="Delete site"
                                      icon={<DeleteIcon />}
                                      onClick={onClickDelete}
                                      showInMenu
                                  />,
                              ]
                            : []),

                        ...(!isBasicRole
                            ? [
                                  <Tooltip title="Manage user roles">
                                      <GridActionsCellItem
                                          label="Manage user roles"
                                          icon={
                                              <AdminPanelSettingsIcon
                                                  fontSize="small"
                                                  color="primary"
                                              />
                                          }
                                          component={Link}
                                          to={`/${FRONTEND_ROUTES.ORDERS}/${FRONTEND_ROUTES.CONTRACT}/${contract.uuid}/${FRONTEND_ROUTES.SITE}/${params.row.id}/${FRONTEND_ROUTES.USER_ROLES}`}
                                      />
                                  </Tooltip>,
                              ]
                            : []),
                    ];

                    return hasLiteralTemplates
                        ? // Add Literal Parcellaire action in third position if there are literal PT
                          actions.toSpliced(
                              2,
                              0,
                              <Tooltip title="Literal Plot Map Tool">
                                  <GridActionsCellItem
                                      label="Literal Plot Map Tool"
                                      icon={
                                          <MapIcon
                                              fontSize="small"
                                              color="primary"
                                          />
                                      }
                                      component={Link}
                                      to={`/${FRONTEND_ROUTES.PARCELLAIRE_LITERAL}/${params.row.id}`}
                                  />
                              </Tooltip>
                          )
                        : actions;
                },
                filterable: false,
            },
            {
                field: "name",
                headerName: "Name",
                minWidth: 250,
                flex: 3,
            },
            {
                field: "displayName",
                headerName: isBasicRole ? "Name" : "Display name",
                minWidth: 250,
                flex: 3,
            },
            {
                field: "country",
                headerName: "Country",
                minWidth: 225,
                flex: 3,
            },
            {
                field: "processingLead",
                headerName: "Processing Lead",
                minWidth: 225,
                flex: 2,
            },
            {
                field: "experimentStatusLabel",
                headerName: "Field map status",
                minWidth: 200,
                flex: 2,
            },
            {
                field: "plotmapStatusLabel",
                headerName: "Plot map status",
                minWidth: 200,
                flex: 2,
            },
        ],
        [
            contract,
            destroy,
            hasLiteralTemplates,
            isBasicRole,
            mutate,
            openSnackbar,
            patch,
        ]
    );
    const columnVisibilityModel = useMemo(
        () => ({
            name: !isBasicRole,
            processingLead: !isBasicRole,
            country: false,
        }),
        [isBasicRole]
    );

    const mergedFetchError = usersError ?? sitesError;
    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;
    if (areSitesLoading || areUsersLoading) return <SiteTableSkeleton />;

    return (
        <Stack>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
            >
                <Typography variant="h6" gutterBottom>
                    Sites
                </Typography>
                <Stack direction="row" spacing={1}>
                    <IconButton size="large" onClick={() => setIsOpen(true)}>
                        <AddCircleOutlinedIcon
                            fontSize="inherit"
                            color="primary"
                        />
                    </IconButton>
                    <Dialog open={isOpen} fullWidth maxWidth="sm">
                        <SiteCreateForm
                            contract={contract}
                            closeModal={() => setIsOpen(false)}
                            mutate={mutate}
                            users={
                                users ??
                                (contract.ProcessingLead
                                    ? [contract.ProcessingLead]
                                    : [])
                            }
                            hasLiteralTemplates={hasLiteralTemplates}
                        />
                    </Dialog>
                </Stack>
            </Stack>

            <Card>
                <Box
                    sx={{
                        height: getDataGridHeight(400, sites.rows.length),
                        width: "100%",
                    }}
                >
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        pageSizeOptions={pageSizeOptions}
                        rowCount={sites.count}
                        paginationMode="server"
                        paginationModel={pagination}
                        onPaginationModelChange={setPage}
                        initialState={{
                            sorting: initialSortingState,
                            columns: {
                                columnVisibilityModel: columnVisibilityModel,
                            },
                        }}
                        disableColumnSelector={isBasicRole}
                        columnVisibilityModel={visibilityModel}
                        onColumnVisibilityModelChange={(newModel) =>
                            setVisibilityModel(newModel)
                        }
                    />
                </Box>
            </Card>
        </Stack>
    );
}

const initialSortingState = { sortModel: [{ field: "name", sort: "asc" }] };
const pageSizeOptions = [100];
